/* eslint-disable react-refresh/only-export-components */
import { Slot } from 'radix-ui';
import { cva, VariantProps } from 'class-variance-authority';

import { ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

const buttonVariants = cva<ButtonVariant>(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutrals-500 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'bg-primary-blue-600 font-semibold text-white hover:bg-primary-blue-700 disabled:bg-neutrals-300 disabled:hover:bg-neutrals-300 disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        secondary:
          'bg-[#545e83] text-white font-semibold hover:bg-[#000e46] disabled:bg-neutrals-300 disabled:hover:bg-neutrals-300 disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        subtle:
          'bg-neutrals-200 text-neutrals-900 font-semibold hover:bg-neutrals-400 disabled:bg-neutrals-300 disabled:hover:bg-neutrals-300 disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        destructive:
          'bg-primary-red-400 text-white hover:bg-primary-red-600 disabled:bg-neutrals-300 disabled:hover:bg-neutrals-300 disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        outline:
          'border border-primary-blue-600 font-semibold bg-inherit hover:bg-primary-blue-600 hover:text-white text-primary-blue-600 disabled:border-neutrals-300 disabled:hover:bg-inherit disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        ghost:
          'hover:bg-neutrals-300 disabled:hover:bg-inherit font-semibold disabled:text-neutrals-700 disabled:hover:cursor-not-allowed',
        'ghost-primary':
          'hover:bg-neutrals-300 disabled:hover:bg-inherit font-semibold text-primary-blue-600 disabled:text-neutrals-700 disabled:hover:cursor-not-allowed',
        'ghost-destructive':
          'hover:bg-neutrals-200 disabled:hover:bg-inherit font-semibold text-primary-red-400 disabled:text-neutrals-700 hover:text-primary-red-500 disabled:hover:cursor-not-allowed',
        link: 'text-black underline-offset-4 hover:underline font-semibold disabled:text-neutrals-700 disabled:underline disabled:hover:cursor-not-allowed',
        'outline-secondary':
          'border border-neutrals-400 font-semibold bg-inherit hover:bg-neutrals-300 text-neutrals-900 disabled:border-neutrals-400 disabled:hover:bg-inherit disabled:hover:cursor-not-allowed disabled:text-neutrals-700',
        icon: 'hover:bg-neutrals-300 font-semibold disabled:hover:bg-inherit disabled:text-neutrals-700 disabled:hover:cursor-not-allowed',
        circle: 'bg-neutrals-200 p-2 rounded-full hover:bg-neutrals-300',
      },
      size: {
        default: 'h-10 px-3 py-2',
        xs: 'h-6 w-6',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        circle: 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  size?: Size;
  variant?: Variant;
};

export type Size = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'circle';

export type Variant =
  | 'default'
  | 'secondary'
  | 'subtle'
  | 'outline'
  | 'outline-secondary'
  | 'destructive'
  | 'ghost'
  | 'ghost-primary'
  | 'ghost-destructive'
  | 'link'
  | 'icon'
  | 'circle';

export type ButtonVariant = {
  variant: Record<Variant, string>;
  size: Record<Size, string>;
};

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
